import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Activity, Save, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const TYMPANOGRAM_TYPES = [
  { value: "A", label: "Type A - Normal", color: "bg-green-100 text-green-700" },
  { value: "As", label: "Type As - Reduced Compliance", color: "bg-yellow-100 text-yellow-700" },
  { value: "Ad", label: "Type Ad - High Compliance", color: "bg-orange-100 text-orange-700" },
  { value: "B", label: "Type B - Flat", color: "bg-red-100 text-red-700" },
  { value: "C", label: "Type C - Negative Pressure", color: "bg-purple-100 text-purple-700" },
];

export default function TympanometryPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [patient, setPatient] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    clinic_id: "",
    right_type: "A",
    right_pressure: "",
    right_compliance: "",
    right_ear_canal_volume: "",
    left_type: "A",
    left_pressure: "",
    left_compliance: "",
    left_ear_canal_volume: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [patientId]);

  const fetchData = async () => {
    try {
      const [clinicsRes] = await Promise.all([
        apiClient.get("/clinics")
      ]);
      setClinics(clinicsRes.data);
      
      if (clinicsRes.data.length > 0) {
        setFormData(prev => ({ ...prev, clinic_id: clinicsRes.data[0].id }));
      }

      if (patientId) {
        const patientRes = await apiClient.get(`/patients/${patientId}`);
        setPatient(patientRes.data);
        setFormData(prev => ({ 
          ...prev, 
          patient_id: patientId,
          clinic_id: patientRes.data.clinic_id 
        }));
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        right_pressure: formData.right_pressure ? parseFloat(formData.right_pressure) : null,
        right_compliance: formData.right_compliance ? parseFloat(formData.right_compliance) : null,
        right_ear_canal_volume: formData.right_ear_canal_volume ? parseFloat(formData.right_ear_canal_volume) : null,
        left_pressure: formData.left_pressure ? parseFloat(formData.left_pressure) : null,
        left_compliance: formData.left_compliance ? parseFloat(formData.left_compliance) : null,
        left_ear_canal_volume: formData.left_ear_canal_volume ? parseFloat(formData.left_ear_canal_volume) : null,
      };

      const response = await apiClient.post("/tympanometry", payload);
      toast.success("Tympanometry test saved!");
      navigate(`/patients/${patientId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save test");
    } finally {
      setSaving(false);
    }
  };

  const getTypeInfo = (type) => {
    return TYMPANOGRAM_TYPES.find(t => t.value === type) || TYMPANOGRAM_TYPES[0];
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 space-y-6">
          <div className="skeleton h-8 w-48"></div>
          <div className="skeleton h-64 w-full rounded-xl"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="tympanometry-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Activity className="w-7 h-7 text-purple-600" />
              Tympanometry Test
            </h1>
            {patient && (
              <p className="text-slate-500">Patient: {patient.name}</p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Right Ear */}
            <Card>
              <CardHeader className="bg-blue-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">R</span>
                  Right Ear
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label>Tympanogram Type *</Label>
                  <Select 
                    value={formData.right_type} 
                    onValueChange={(v) => setFormData({...formData, right_type: v})}
                  >
                    <SelectTrigger data-testid="right-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYMPANOGRAM_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div className="flex items-center gap-2">
                            <Badge className={t.color}>{t.value}</Badge>
                            <span>{t.label.split(" - ")[1]}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Badge className={`mt-2 ${getTypeInfo(formData.right_type).color}`}>
                    {getTypeInfo(formData.right_type).label}
                  </Badge>
                </div>

                <div>
                  <Label>Middle Ear Pressure (daPa)</Label>
                  <Input
                    type="number"
                    placeholder="-100 to +50 normal"
                    value={formData.right_pressure}
                    onChange={(e) => setFormData({...formData, right_pressure: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Normal: -100 to +50 daPa</p>
                </div>

                <div>
                  <Label>Static Compliance (ml)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="0.3 - 1.6 normal"
                    value={formData.right_compliance}
                    onChange={(e) => setFormData({...formData, right_compliance: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Normal: 0.3 - 1.6 ml</p>
                </div>

                <div>
                  <Label>Ear Canal Volume (ml)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="0.6 - 2.0 normal"
                    value={formData.right_ear_canal_volume}
                    onChange={(e) => setFormData({...formData, right_ear_canal_volume: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Normal: 0.6 - 2.0 ml</p>
                </div>
              </CardContent>
            </Card>

            {/* Left Ear */}
            <Card>
              <CardHeader className="bg-red-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <span className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">L</span>
                  Left Ear
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label>Tympanogram Type *</Label>
                  <Select 
                    value={formData.left_type} 
                    onValueChange={(v) => setFormData({...formData, left_type: v})}
                  >
                    <SelectTrigger data-testid="left-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYMPANOGRAM_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div className="flex items-center gap-2">
                            <Badge className={t.color}>{t.value}</Badge>
                            <span>{t.label.split(" - ")[1]}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Badge className={`mt-2 ${getTypeInfo(formData.left_type).color}`}>
                    {getTypeInfo(formData.left_type).label}
                  </Badge>
                </div>

                <div>
                  <Label>Middle Ear Pressure (daPa)</Label>
                  <Input
                    type="number"
                    placeholder="-100 to +50 normal"
                    value={formData.left_pressure}
                    onChange={(e) => setFormData({...formData, left_pressure: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Normal: -100 to +50 daPa</p>
                </div>

                <div>
                  <Label>Static Compliance (ml)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="0.3 - 1.6 normal"
                    value={formData.left_compliance}
                    onChange={(e) => setFormData({...formData, left_compliance: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Normal: 0.3 - 1.6 ml</p>
                </div>

                <div>
                  <Label>Ear Canal Volume (ml)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="0.6 - 2.0 normal"
                    value={formData.left_ear_canal_volume}
                    onChange={(e) => setFormData({...formData, left_ear_canal_volume: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Normal: 0.6 - 2.0 ml</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notes and Submit */}
          <Card className="mt-6">
            <CardContent className="p-6">
              <Label>Clinical Notes</Label>
              <Textarea
                className="mt-2"
                placeholder="Any observations, findings, or recommendations..."
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />

              <div className="flex justify-end gap-3 mt-6">
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={saving}
                  data-testid="save-tympanometry-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Test"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Reference Guide */}
          <Card className="mt-6 bg-slate-50">
            <CardHeader>
              <CardTitle className="text-lg">Tympanogram Type Reference</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {TYMPANOGRAM_TYPES.map(t => (
                  <div key={t.value} className="p-3 bg-white rounded-lg border">
                    <Badge className={t.color}>{t.value}</Badge>
                    <p className="text-sm font-medium mt-2">{t.label.split(" - ")[1]}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {t.value === "A" && "Normal middle ear function"}
                      {t.value === "As" && "Otosclerosis, scarred TM"}
                      {t.value === "Ad" && "Ossicular discontinuity"}
                      {t.value === "B" && "Fluid, perforation, cerumen"}
                      {t.value === "C" && "Eustachian tube dysfunction"}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </DashboardLayout>
  );
}
